import { useState } from 'react'
import type { ClientAction, PlayerView } from '@sh/shared'
import { ConfirmButton } from '../../components/Confirm.js'
import { DiceIcon } from '../../components/Icons.js'
import { QrCode } from '../../components/QrCode.js'
import { WaitingOn } from '../../components/Waiting.js'
import { useDeployment } from '../../net/deployment.js'
import { DevBots } from './DevBots.js'
import { useSkinCatalog } from '../../skin/catalog.js'

/**
 * Unbiased draw below `max`, from the platform's cryptographic generator where
 * there is one. `Math.random()` would pass unnoticed, but this deal decides who
 * holds the first Presidency in a game about who trusts whom, so it is worth
 * the few lines it takes to make "the phone picked it" an honest answer.
 */
function drawBelow(max: number): number {
  const source = globalThis.crypto
  if (!source?.getRandomValues) return Math.floor(Math.random() * max)
  // The tail of the 32-bit range that does not divide evenly is thrown away and
  // drawn again, so that no seat comes out fractionally likelier than another.
  const limit = Math.floor(0x100000000 / max) * max
  const buf = new Uint32Array(1)
  for (;;) {
    source.getRandomValues(buf)
    const n = buf[0] as number
    if (n < limit) return n % max
  }
}

/** Fisher-Yates, over a copy. */
function shuffle(ids: readonly string[]): string[] {
  const next = [...ids]
  for (let i = next.length - 1; i > 0; i--) {
    const j = drawBelow(i + 1)
    const a = next[i] as string
    next[i] = next[j] as string
    next[j] = a
  }
  return next
}

const sameOrder = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i])

/** The same players in any arrangement - all the engine asks of a seat order. */
function samePlayers(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false
  const x = [...a].sort()
  const y = [...b].sort()
  return x.every((id, i) => id === y[i])
}

/**
 * Seats are arranged to match the real table before the game starts, because
 * the Presidency passes clockwise and "next player" has to mean the person
 * actually sitting there.
 */
export function LobbyPhase({
  view,
  code,
  act,
  leave,
}: {
  view: PlayerView
  code: string
  act: (a: ClientAction) => void
  /** Hands this seat back and takes the device to the main screen. */
  leave: () => void
}) {
  const skins = useSkinCatalog().skins
  const deployment = useDeployment()
  const [copied, setCopied] = useState(false)
  const [boardCopied, setBoardCopied] = useState(false)
  /** The board QR stays folded away so it cannot be mistaken for the join one. */
  const [boardTipOpen, setBoardTipOpen] = useState(false)
  /** The arrangement the last deal replaced, kept so one tap can be taken back. */
  const [dealt, setDealt] = useState<string[] | null>(null)
  const isHost = view.you?.isHost === true
  const practice = view.config.practice === true
  const count = view.players.length
  const joinUrl = `${window.location.origin}/join/${code}`
  const boardUrl = `${window.location.origin}/board/${code}`

  const move = (index: number, delta: number) => {
    const next = [...view.seatOrder]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    const a = next[index] as string
    next[index] = next[target] as string
    next[target] = a
    // A hand-made tweak is the new intent, so the deal it followed stops being
    // the thing an undo would go back to.
    setDealt(null)
    act({ type: 'host_reorder_seats', seatOrder: next })
  }

  /**
   * The Presidency passes down this list, so shuffling the list is how a table
   * that is not sitting in any particular circle gets a random turn order -
   * the same thing as dealing the placard at random, done once, up front.
   */
  const deal = () => {
    const current = view.seatOrder
    // A deal that leaves everyone exactly where they were is indistinguishable
    // from a dead button - one table in a hundred and twenty, at five seats - so
    // it is drawn again. Past one seat a different arrangement always exists.
    let next = shuffle(current)
    for (let tries = 0; tries < 20 && sameOrder(next, current); tries++) next = shuffle(current)
    setDealt([...current])
    act({ type: 'host_reorder_seats', seatOrder: next })
  }

  /** Puts the table back the way the host had arranged it before the deal. */
  const undoDeal = () => {
    if (!dealt) return
    setDealt(null)
    act({ type: 'host_reorder_seats', seatOrder: dealt })
  }

  // The engine only accepts a seat order naming exactly the players present, so
  // somebody joining or walking out quietly retires the offer to undo.
  const canUndo = dealt !== null && samePlayers(dealt, view.seatOrder)

  return (
    <div className="stack">
      {/* Nobody else can join a practice table, so there is nothing to invite with. */}
      {!practice && (
        <section className="card card--code">
          <h2 className="card__title">Room code</h2>
          <div className="code" data-testid="lobby-code">
            {code}
          </div>
          <QrCode value={joinUrl} size={148} />
          <button
            type="button"
            className="btn btn--ghost"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(joinUrl)
                setCopied(true)
                window.setTimeout(() => setCopied(false), 2000)
              } catch {
                setCopied(false)
              }
            }}
          >
            {copied ? 'link copied' : 'copy join link'}
          </button>
        </section>
      )}

      <section className="card">
        <h2 className="card__title">
          Seats <span className="card__count">{count}/10</span>
        </h2>
        <p className="card__hint">
          {isHost
            ? 'Put these in the order people are actually sitting, clockwise. The Presidency passes down this list.'
            : 'The host is arranging these to match the table.'}
        </p>
        {isHost && count > 1 && (
          <div className="arrange__deal">
            {canUndo && (
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={undoDeal}
                data-testid="undo-randomize-seats"
              >
                undo
              </button>
            )}
            <button
              type="button"
              className="btn btn--ghost btn--small btn--withicon"
              onClick={deal}
              data-testid="randomize-seats"
            >
              Random
              <DiceIcon />
            </button>
          </div>
        )}
        <ol className="arrange" data-testid="lobby-seats">
          {view.seatOrder.map((id, index) => {
            const player = view.players.find((p) => p.id === id)
            if (!player) return null
            return (
              <li key={id} className="arrange__row" data-testid={`lobby-seat-${player.name}`}>
                <span className="arrange__index">{index + 1}</span>
                <span className="arrange__name">
                  {player.name}
                  {id === view.you?.id && <span className="badge badge--you">you</span>}
                  {id === view.hostId && <span className="badge badge--host">host</span>}
                  {!player.connected && <span className="badge badge--off">offline</span>}
                </span>
                {isHost && (
                  <span className="arrange__controls">
                    <button
                      type="button"
                      className="btn btn--icon"
                      aria-label={`move ${player.name} earlier`}
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="btn btn--icon"
                      aria-label={`move ${player.name} later`}
                      disabled={index === count - 1}
                      onClick={() => move(index, 1)}
                    >
                      ▼
                    </button>
                    {id !== view.you?.id && (
                      <ConfirmButton
                        className="btn--icon btn--danger"
                        confirmLabel="✓"
                        onConfirm={() => act({ type: 'host_kick', targetId: id })}
                      >
                        ✕
                      </ConfirmButton>
                    )}
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      </section>

      {/*
        Directly under the seat list it changes, so one tap produces a visible
        change an inch above it. Below the start button it would sit under the
        very label it exists to satisfy.
      */}
      {isHost && deployment.env === 'dev' && (
        <DevBots count={count} maxBots={deployment.maxBots} />
      )}

      {/*
        Shut until somebody asks for it. Two QR codes on one lobby screen is
        one too many: the one above seats a player, this one only opens a
        spectating board, and a table holding a phone up to the wrong square
        ends up with ten boards and nobody at the table. The title says what
        is behind it, which is all a screen that is already the right screen
        needs to hear.
      */}
      {/* Nobody else is coming to a practice table, so there is no board to put up. */}
      {!practice && (
        <section
          className={`card card--tip ${boardTipOpen ? 'is-open' : 'is-shut'}`}
          data-testid="board-tip"
        >
          <h2 className="card__title">
            <button
              type="button"
              className="tip__toggle"
              aria-expanded={boardTipOpen}
              aria-controls="board-tip-body"
              onClick={() => setBoardTipOpen((o) => !o)}
              data-testid="board-tip-toggle"
            >
              Put the board on a bigger screen
              <span className={`tip__chevron ${boardTipOpen ? 'is-up' : ''}`} aria-hidden="true" />
            </button>
          </h2>
          <div className="tip__body" id="board-tip-body">
            <p className="card__hint">
              Optional, and worth it. Type this into a laptop, tablet or TV in the room — or point its
              camera at the code:
            </p>
            <p className="tip__url">
              <code data-testid="board-url">{boardUrl}</code>
            </p>
            <QrCode value={boardUrl} size={104} />
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(boardUrl)
                  setBoardCopied(true)
                  window.setTimeout(() => setBoardCopied(false), 2000)
                } catch {
                  setBoardCopied(false)
                }
              }}
            >
              {boardCopied ? 'board link copied' : 'copy board link'}
            </button>
            <p className="card__hint">
              It holds no seat and receives no secrets, so it is safe to leave facing the room. Use as
              many screens as you like.
            </p>
          </div>
        </section>
      )}

      {/*
        A picker over one skin is not a choice, it is a button that does
        nothing, so it is not shown at all: a deployment with a single skin - or
        with skins turned off - simply plays on what it has.
      */}
      {isHost && skins.length > 1 && (
        <section className="card">
          <h2 className="card__title">Table settings</h2>

          <div className="field">
            <span className="field__label">Skin (applies to every screen)</span>
            <div className="choices choices--row" data-testid="skin-choices">
              {skins.map((skin) => (
                <button
                  key={skin.id}
                  type="button"
                  className={`btn btn--choice ${view.config.skin === skin.id ? 'is-selected' : ''}`}
                  onClick={() => act({ type: 'host_set_config', config: { skin: skin.id } })}
                >
                  {skin.name}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {isHost ? (
        <button
          type="button"
          className="btn btn--primary btn--block"
          disabled={count < 5}
          onClick={() => act({ type: 'host_start_game' })}
          data-testid="start-game"
        >
          {count < 5 ? `Need ${5 - count} more player${count === 4 ? '' : 's'}` : `Start with ${count}`}
        </button>
      ) : (
        <WaitingOn who={view.players.find((p) => p.id === view.hostId)?.name}>
          Waiting for the host to start
        </WaitingOn>
      )}

      {/*
        The same act as the mark in the corner, spelled out for the one screen
        where somebody is deciding whether to stay: the seat goes back and the
        device goes home. Two taps, because a stray thumb on the way to the
        start button should not cost somebody their place at the table.
      */}
      <ConfirmButton
        className="btn--ghost btn--block"
        confirmLabel="Press again to leave"
        onConfirm={leave}
        testId="leave-lobby"
      >
        Leave this lobby
      </ConfirmButton>
    </div>
  )
}
